import React, { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

interface PasswordStrength {
  score: number;
  requirements: {
    minLength: boolean;
    hasUpperCase: boolean;
    hasLowerCase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  };
}

const calculatePasswordStrength = (password: string): PasswordStrength => {
  const requirements = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  const score = Object.values(requirements).filter(Boolean).length;
  return { score, requirements };
};

const STRENGTH_TONE: Record<number, 'error' | 'warning' | 'info' | 'success'> = {
  0: 'error', 1: 'error', 2: 'error',
  3: 'warning',
  4: 'info',
  5: 'success',
};

const STRENGTH_LABEL: Record<number, string> = {
  0: 'Too weak', 1: 'Too weak', 2: 'Weak',
  3: 'Fair',
  4: 'Good',
  5: 'Strong',
};

const REQUIREMENTS: { key: keyof PasswordStrength['requirements']; label: string }[] = [
  { key: 'minLength', label: 'At least 8 characters' },
  { key: 'hasUpperCase', label: 'One uppercase letter' },
  { key: 'hasLowerCase', label: 'One lowercase letter' },
  { key: 'hasNumber', label: 'One number' },
  { key: 'hasSpecialChar', label: 'One special character' },
];

export const RegisterPageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoading, isAuthenticated } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [touched, setTouched] = useState({
    username: false,
    email: false,
    password: false,
    confirmPassword: false,
  });

  const [errors, setErrors] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const passwordStrength = calculatePasswordStrength(formData.password);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const newErrors = { username: '', email: '', password: '', confirmPassword: '' };

    if (touched.username) {
      if (!formData.username) {
        newErrors.username = 'Username is required';
      } else if (formData.username.length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
      } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
        newErrors.username = 'Letters, numbers, and underscores only';
      }
    }

    if (touched.email) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Enter a valid email address';
      }
    }

    if (touched.password) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (new TextEncoder().encode(formData.password).length > 72) {
        newErrors.password = 'Password is too long (bcrypt limit is 72 bytes)';
      }
    }

    if (touched.confirmPassword) {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
  }, [formData, touched]);

  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleBlur = (field: keyof typeof touched) => () => {
    setTouched({ ...touched, [field]: true });
  };

  const isFormValid = () => (
    formData.username &&
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    !errors.username &&
    !errors.email &&
    !errors.password &&
    !errors.confirmPassword &&
    passwordStrength.score >= 3
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ username: true, email: true, password: true, confirmPassword: true });

    if (!isFormValid()) {
      showError('Fix the validation errors before submitting.');
      return;
    }

    try {
      await register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
      });
      showSuccess('Registration submitted. Your account is awaiting approval.');
      setFormData({ username: '', email: '', password: '', confirmPassword: '' });
      setTouched({ username: false, email: false, password: false, confirmPassword: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed. Try again.';
      showError(message);
    }
  };

  const tone = STRENGTH_TONE[passwordStrength.score];
  const label = STRENGTH_LABEL[passwordStrength.score];

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 2, md: 6 } }}>
      <Card sx={{ width: '100%', maxWidth: 460 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src="/images/DIRTcarLogo.png" alt="DIRTcar" style={{ height: 48, width: 'auto' }} />
          </Box>
          <Typography variant="h5" align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
            Create account
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Accounts require admin approval before they can sign in.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Username"
                value={formData.username}
                onChange={handleInputChange('username')}
                onBlur={handleBlur('username')}
                error={touched.username && !!errors.username}
                helperText={touched.username && errors.username}
                disabled={isLoading}
                autoComplete="username"
                autoFocus
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                onBlur={handleBlur('email')}
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                disabled={isLoading}
                autoComplete="email"
                required
                fullWidth
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                onBlur={handleBlur('password')}
                error={touched.password && !!errors.password}
                helperText={touched.password && errors.password}
                disabled={isLoading}
                autoComplete="new-password"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Collapse in={touched.password && formData.password.length > 0}>
                <Box sx={{ pt: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Password strength
                    </Typography>
                    <Typography variant="caption" color={`${tone}.main`} sx={{ fontWeight: 600 }}>
                      {label}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(passwordStrength.score / 5) * 100}
                    color={tone}
                    sx={{ height: 4, borderRadius: 2, mb: 1 }}
                  />
                  <Stack spacing={0.25}>
                    {REQUIREMENTS.map((req) => {
                      const met = passwordStrength.requirements[req.key];
                      return (
                        <Typography
                          key={req.key}
                          variant="caption"
                          sx={{ color: met ? 'success.main' : 'text.secondary' }}
                        >
                          {met ? '✓' : '○'} {req.label}
                        </Typography>
                      );
                    })}
                  </Stack>
                </Box>
              </Collapse>

              <TextField
                label="Confirm password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                onBlur={handleBlur('confirmPassword')}
                error={touched.confirmPassword && !!errors.confirmPassword}
                helperText={touched.confirmPassword && errors.confirmPassword}
                disabled={isLoading}
                autoComplete="new-password"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading || !isFormValid()}
                fullWidth
              >
                {isLoading ? 'Submitting' : 'Register'}
              </Button>
            </Stack>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2.5 }}>
            <MuiLink component={RouterLink} to="/login" variant="body2" underline="hover">
              Already have an account? Sign in
            </MuiLink>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

